import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Stack,
  Avatar,
  Chip,
  Divider,
  Alert,
  InputAdornment,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import EngineeringIcon from "@mui/icons-material/Engineering";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { calculateStock, pluralizeUnit } from "../utils";

/**
 * Modal para consumir stock
 */
const ConsumeStockModal = ({ open, onClose, product, technicians = [], locations = [], selectedTechnician = null, selectedWarehouse = null, onSubmit }) => {
  const [formData, setFormData] = useState({
    fromLocationType: "main_warehouse", // main_warehouse, technician, dangerous_goods
    fromLocationId: null, // ID específico del almacén
    fromTechnicianId: null,
    quantity: 0,
    reason: "",
  });

  const [error, setError] = useState("");

  useEffect(() => {
    if (product && locations) {
      // Si hay un técnico preseleccionado, usar su inventario
      if (selectedTechnician) {
        setFormData({
          fromLocationType: "technician",
          fromLocationId: null,
          fromTechnicianId: selectedTechnician._id,
          quantity: 0,
          reason: "",
        });
      } else if (selectedWarehouse) {
        // Si hay un almacén preseleccionado, usarlo
        setFormData({
          fromLocationType: "main_warehouse",
          fromLocationId: selectedWarehouse._id,
          fromTechnicianId: null,
          quantity: 0,
          reason: "",
        });
      } else {
        // Seleccionar el primer almacén disponible por defecto
        const activeWarehouses = locations.filter(loc => loc.type === "main_warehouse" && loc.status === "active");
        const firstWarehouse = activeWarehouses.length > 0 ? activeWarehouses[0] : null;
        
        setFormData({
          fromLocationType: "main_warehouse",
          fromLocationId: firstWarehouse?._id || null,
          fromTechnicianId: null,
          quantity: 0,
          reason: "",
        });
      }
      setError("");
    }
  }, [product, technicians, locations, selectedTechnician, selectedWarehouse]);

  if (!product) return null;

  const { warehouse: totalWarehouseStock, technicians: techStock, total } = calculateStock(product);

  // Obtener stock disponible según el origen seleccionado
  const getAvailableStock = () => {
    if (formData.fromLocationType === "main_warehouse" && formData.fromLocationId) {
      // Buscar el stock del almacén específico
      const warehouse = product.warehouses?.find(
        (w) => (w.locationId?.toString() || w.locationId) === formData.fromLocationId
      );
      return warehouse?.stock || 0;
    } else if (formData.fromLocationType === "technician" && formData.fromTechnicianId) {
      const tech = product.technicians?.find(
        (t) => (t.technicianId?.toString() || t.technicianId) === formData.fromTechnicianId
      );
      return tech?.stock || 0;
    } else if (formData.fromLocationType === "dangerous_goods") {
      return product.hazardousStock || 0;
    }
    return 0;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError("");
  };

  const validateConsume = () => {
    // Validar que se haya seleccionado un almacén si es necesario
    if (formData.fromLocationType === "main_warehouse" && !formData.fromLocationId) {
      setError("Debes seleccionar un almacén de origen");
      return false;
    }

    // Validar que se haya seleccionado un técnico si es necesario
    if (formData.fromLocationType === "technician" && !formData.fromTechnicianId) {
      setError("Debes seleccionar un técnico de origen");
      return false;
    }

    if (formData.quantity <= 0) {
      setError("La cantidad debe ser mayor a 0");
      return false;
    }

    // Validar stock disponible
    const maxAvailable = getAvailableStock();
    if (formData.quantity > maxAvailable) {
      setError(
        `Stock insuficiente. Disponible: ${maxAvailable} ${pluralizeUnit(product.unit, maxAvailable)}`
      );
      return false;
    }

    return true;
  };

  const handleSubmit = () => {
    if (!validateConsume()) return;

    onSubmit({
      itemId: product._id,
      fromLocationType: formData.fromLocationType,
      fromLocationId: formData.fromLocationId,
      fromTechnicianId: formData.fromTechnicianId,
      quantity: formData.quantity,
      reason: formData.reason || null,
    });
  };

  const locationTypes = [
    { value: "main_warehouse", label: "Almacén Principal", icon: <WarehouseIcon /> },
    { value: "technician", label: "Técnico", icon: <EngineeringIcon /> },
    { value: "dangerous_goods", label: "Residuos Peligrosos", icon: <DeleteForeverIcon /> },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 12px 48px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "error.main",
          color: "white",
          pb: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              width: 48,
              height: 48,
            }}
          >
            <DeleteOutlineIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Consumir Stock
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {product.name}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Información del producto */}
        <Box
          sx={{
            bgcolor: "grey.50",
            borderRadius: 2,
            p: 2,
            mb: 3,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            display="block"
            mb={1}
          >
            Stock Actual
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="space-around">
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                {totalWarehouseStock}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                En Almacenes
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" fontWeight={700} color="info.main">
                {techStock}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Con Técnicos
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" fontWeight={700} color="success.main">
                {total}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Formulario de consumo */}
        <Stack spacing={3}>
          {/* Origen - Tipo de ubicación */}
          <FormControl fullWidth>
            <InputLabel>Desde</InputLabel>
            <Select
              value={formData.fromLocationType}
              onChange={(e) => {
                handleChange("fromLocationType", e.target.value);
                handleChange("fromTechnicianId", null);
                handleChange("fromLocationId", null);
                // Seleccionar el primer almacén si se elige main_warehouse
                if (e.target.value === "main_warehouse") {
                  const firstWarehouse = locations.find(loc => loc.type === "main_warehouse");
                  handleChange("fromLocationId", firstWarehouse?._id || null);
                }
              }}
              label="Desde"
            >
              {locationTypes.map((loc) => (
                <MenuItem key={loc.value} value={loc.value}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    {loc.icon}
                    <Typography>{loc.label}</Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Seleccionar almacén de origen (si aplica) */}
          {formData.fromLocationType === "main_warehouse" && (
            <FormControl fullWidth>
              <InputLabel>Almacén de origen</InputLabel>
              <Select
                value={formData.fromLocationId || ""}
                onChange={(e) => handleChange("fromLocationId", e.target.value)}
                label="Almacén de origen"
              >
                {locations
                  .filter(loc => loc.type === "main_warehouse" && loc.status === "active")
                  .map((location) => {
                    // Buscar el stock de este almacén en el producto
                    const warehouseStock = product.warehouses?.find(
                      (w) => (w.locationId?.toString() || w.locationId) === location._id
                    );
                    const stock = warehouseStock?.stock || 0;
                    
                    return (
                      <MenuItem key={location._id} value={location._id}>
                        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ width: "100%" }}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <WarehouseIcon fontSize="small" />
                            <Typography>{location.name || "Almacén Principal"}</Typography>
                          </Stack>
                          <Chip 
                            label={`${stock} ${pluralizeUnit(product.unit, stock)}`} 
                            size="small" 
                            color={stock > 0 ? "success" : "default"}
                          />
                        </Stack>
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>
          )}

          {/* Seleccionar técnico de origen (si aplica) */}
          {formData.fromLocationType === "technician" && (
            <FormControl fullWidth>
              <InputLabel>Técnico de origen</InputLabel>
              <Select
                value={formData.fromTechnicianId || ""}
                onChange={(e) => handleChange("fromTechnicianId", e.target.value)}
                label="Técnico de origen"
              >
                {technicians.map((tech) => {
                  const techStock = product.technicians?.find(
                    (t) => (t.technicianId?.toString() || t.technicianId) === tech._id
                  );
                  const stock = techStock?.stock || 0;
                  return (
                    <MenuItem key={tech._id} value={tech._id}>
                      <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ width: "100%" }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <EngineeringIcon fontSize="small" />
                          <Typography>{tech.name}</Typography>
                        </Stack>
                        <Chip 
                          label={`${stock} ${pluralizeUnit(product.unit, stock)}`} 
                          size="small" 
                          color={stock > 0 ? "success" : "default"}
                        />
                      </Stack>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}

          {/* Cantidad */}
          <TextField
            label="Cantidad a consumir"
            type="number"
            value={formData.quantity}
            onChange={(e) => handleChange("quantity", parseFloat(e.target.value) || 0)}
            fullWidth
            helperText={`Disponible: ${getAvailableStock()} ${pluralizeUnit(product.unit, getAvailableStock())}`}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Chip label={pluralizeUnit(product.unit, formData.quantity || 0)} size="small" />
                </InputAdornment>
              ),
            }}
            inputProps={{
              min: 0,
              max: getAvailableStock(),
              step: product.unit === "ml" || product.unit === "lt" ? 0.1 : 1,
            }}
          />

          {/* Notas */}
          <TextField
            label="Razón del consumo (opcional)"
            value={formData.reason}
            onChange={(e) => handleChange("reason", e.target.value)}
            multiline
            rows={2}
            fullWidth
            placeholder="Ej: Uso en servicio, desperdicio, etc..."
          />

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined" size="large">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="error"
          size="large"
          startIcon={<DeleteOutlineIcon />}
          disabled={!formData.quantity || (formData.fromLocationType === "technician" && !formData.fromTechnicianId)}
        >
          Consumir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConsumeStockModal;

